'use client';

import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  Search,
  Settings,
} from 'lucide-react';
import * as React from 'react';
import { Button, ClearButton } from '@/components/atoms/buttons/Button';
import { Avatar } from '@/components/molecules/data/Avatar';
import { Kbd } from '@/components/molecules/data/Kbd';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

const DEFAULT_COMMAND_MENU_MAX_HEIGHT = 336;
const DEFAULT_COMMAND_MENU_PLACEHOLDER = 'Type a command or search...';
const COMMAND_MENU_SHELL_CLASS_NAME =
  'w-full max-w-[508px] overflow-hidden rounded-xl border border-input bg-background shadow-lg';

function getCommandMenuMaxHeight(maxHeight: number): number {
  return Number.isFinite(maxHeight) && maxHeight >= 0
    ? maxHeight
    : DEFAULT_COMMAND_MENU_MAX_HEIGHT;
}

// ============================================
// Types
// ============================================

export interface CommandMenuOption {
  /** Unique identifier for the option */
  id: string;
  /** Display label for the option */
  label: string;
  /** Optional subtitle (e.g., @username, email) */
  subtitle?: string;
  /** Optional icon to display (mutually exclusive with avatar) */
  icon?: React.ReactNode;
  /** Optional avatar configuration */
  avatar?: {
    src?: string;
    fallback?: string;
  };
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Keyboard shortcut to display */
  shortcut?: string;
}

export interface CommandMenuGroup {
  /** Unique identifier for the group */
  id: string;
  /** Group label/heading */
  label: string;
  /** Options in this group */
  options: CommandMenuOption[];
}

export interface CommandMenuFooterHint {
  /** Icon or keyboard key indicator */
  keys: React.ReactNode;
  /** Text describing the action */
  label: string;
}

export interface CommandMenuProps {
  /** Whether the command menu is open */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Text to show when no results found */
  emptyText?: string;
  /** Empty state content (replaces emptyText when provided) */
  emptyState?: React.ReactNode;
  /** Whether to render results and the empty state below search */
  showResults?: boolean;
  /** Flat list of options */
  options?: CommandMenuOption[];
  /** Grouped options */
  groups?: CommandMenuGroup[];
  /** Callback when an option is selected */
  onSelect?: (id: string, option: CommandMenuOption) => void;
  /** Whether to show keyboard shortcut badge in input */
  showShortcut?: boolean;
  /** Keyboard shortcut text to display (auto-detects platform when omitted) */
  shortcutText?: string;
  /** Whether to show the footer with navigation hints */
  showFooter?: boolean;
  /** Custom footer hints */
  footerHints?: CommandMenuFooterHint[];
  /** Whether to show settings button in footer */
  showSettingsButton?: boolean;
  /** Callback when settings button is clicked */
  onSettingsClick?: () => void;
  /** Additional class name for the dialog */
  className?: string;
  /** Maximum height of the list */
  maxHeight?: number;
  /** Custom filter function */
  filter?: (value: string, search: string) => number;
  /** Children for custom content */
  children?: React.ReactNode;
}

// ============================================
// Platform detection
// ============================================

function usePlatformShortcut() {
  const [shortcut, setShortcut] = React.useState('\u2318/');
  React.useEffect(() => {
    if (!/Mac|iPhone|iPad/i.test(navigator.userAgent)) {
      setShortcut('Ctrl /');
    }
  }, []);
  return shortcut;
}

// ============================================
// Keyboard Shortcut Badge (uses Kbd wrapper)
// ============================================

function CommandShortcutBadge({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Kbd variant="solid" size="sm" className={cn('shadow-xs', className)}>
      {children}
    </Kbd>
  );
}
CommandShortcutBadge.displayName = 'CommandMenu.ShortcutBadge';

// ============================================
// Footer Navigation Icon (uses Kbd wrapper)
// ============================================

function FooterNavIcon({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Kbd variant="footer" size="sm" className={cn('p-1.5', className)}>
      {children}
    </Kbd>
  );
}
FooterNavIcon.displayName = 'CommandMenu.FooterNavIcon';

function FooterNavText({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'text-sm font-semibold text-secondary-foreground',
        className,
      )}
    >
      {children}
    </span>
  );
}
FooterNavText.displayName = 'CommandMenu.FooterNavText';

// ============================================
// Default Footer Hints
// ============================================

const defaultFooterHints: CommandMenuFooterHint[] = [
  {
    keys: (
      <div className="flex items-center gap-1">
        <FooterNavIcon>
          <ArrowUp className="size-2.5 text-secondary-foreground" />
        </FooterNavIcon>
        <FooterNavIcon>
          <ArrowDown className="size-2.5 text-secondary-foreground" />
        </FooterNavIcon>
      </div>
    ),
    label: 'to navigate',
  },
  {
    keys: (
      <FooterNavIcon>
        <CornerDownLeft className="size-2.5 text-secondary-foreground" />
      </FooterNavIcon>
    ),
    label: 'to select',
  },
  {
    keys: (
      <FooterNavIcon className="px-1.5 py-1">
        <span className="text-xs font-semibold text-secondary-foreground">
          esc
        </span>
      </FooterNavIcon>
    ),
    label: 'to close',
  },
];

// ============================================
// Command Menu Footer
// ============================================

export interface CommandMenuFooterProps {
  hints?: CommandMenuFooterHint[];
  showSettingsButton?: boolean;
  onSettingsClick?: () => void;
  className?: string;
}

function CommandMenuFooter({
  hints = defaultFooterHints,
  showSettingsButton = false,
  onSettingsClick,
  className,
}: CommandMenuFooterProps) {
  return (
    <div
      data-slot="command-menu-footer"
      className={cn(
        'flex items-center gap-3 border-t border-border bg-card/80 py-2 pe-2 ps-[18px] supports-backdrop-filter:backdrop-blur',
        className,
      )}
    >
      <div className="hidden min-w-0 flex-1 flex-wrap items-start gap-x-4 gap-y-2 min-[400px]:[@media(hover:hover)]:flex">
        {hints.map((hint) => (
          <div key={hint.label} className="flex items-center gap-2">
            {hint.keys}
            <FooterNavText>{hint.label}</FooterNavText>
          </div>
        ))}
      </div>
      {showSettingsButton && (
        <Button
          appearance="ghost"
          variant="secondary"
          size="icon"
          className="group ms-auto rounded-full bg-muted"
          onClick={onSettingsClick}
          aria-label="Settings"
        >
          <Settings className="size-5 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
CommandMenuFooter.displayName = 'CommandMenu.Footer';

// ============================================
// Command Menu Item
// ============================================

export interface CommandMenuItemProps
  extends Omit<
    React.ComponentProps<typeof CommandItem>,
    'onSelect' | 'showCheckIndicator' | 'value'
  > {
  option: CommandMenuOption;
  onSelect?: (id: string, option: CommandMenuOption) => void;
}

function CommandMenuItem({
  option,
  onSelect,
  className,
  ...props
}: CommandMenuItemProps) {
  return (
    <CommandItem
      value={option.id}
      disabled={option.disabled}
      showCheckIndicator={false}
      onSelect={() => onSelect?.(option.id, option)}
      className={cn('flex cursor-pointer items-center gap-2', className)}
      {...props}
    >
      {option.avatar && (
        <Avatar
          src={option.avatar.src}
          fallback={option.avatar.fallback || option.label.charAt(0)}
          size="sm"
          className="size-6 shrink-0"
        />
      )}
      {option.icon && !option.avatar && (
        <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
          {option.icon}
        </span>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2 text-sm leading-5">
        <span className="min-w-0 truncate font-normal text-foreground">
          {option.label}
        </span>
        {option.subtitle && (
          <span className="min-w-0 truncate font-normal text-muted-foreground">
            {option.subtitle}
          </span>
        )}
      </div>
      {option.shortcut && (
        <CommandShortcutBadge className="group-data-[selected=true]/command-item:bg-primary/10 group-data-[selected=true]/command-item:text-primary">
          {option.shortcut}
        </CommandShortcutBadge>
      )}
    </CommandItem>
  );
}
CommandMenuItem.displayName = 'CommandMenu.Item';

// ============================================
// Command Menu Group Header
// ============================================

function CommandMenuGroupHeader({
  children,
  className,
  ...props
}: React.ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="command-menu-group-header"
      className={cn(
        'px-2 py-1.5 text-xs font-medium text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
}
CommandMenuGroupHeader.displayName = 'CommandMenu.GroupHeader';

// ============================================
// Shared Command Menu Body (internal)
// ============================================

interface CommandMenuBodyProps {
  children?: React.ReactNode;
  groups: CommandMenuGroup[];
  options: CommandMenuOption[];
  onSelect?: (id: string, option: CommandMenuOption) => void;
  onClose?: () => void;
  placeholder: string;
  emptyText: string;
  emptyState?: React.ReactNode;
  showResults: boolean;
  showShortcut: boolean;
  shortcutText: string;
  showFooter: boolean;
  footerHints?: CommandMenuFooterHint[];
  showSettingsButton: boolean;
  onSettingsClick?: () => void;
  maxHeight: number;
  reserveResultsSpace?: boolean;
  filter?: (value: string, search: string) => number;
}

interface CommandMenuBodyContentProps {
  children?: React.ReactNode;
  groups: CommandMenuGroup[];
  options: CommandMenuOption[];
  optionRenderer: (option: CommandMenuOption) => React.ReactNode;
}

interface CommandMenuGroupSectionProps {
  group: CommandMenuGroup;
  optionRenderer: (option: CommandMenuOption) => React.ReactNode;
}

function CommandMenuGroupSection({
  group,
  optionRenderer,
}: CommandMenuGroupSectionProps) {
  const headingId = React.useId();

  return (
    <CommandGroup aria-labelledby={headingId} className="px-2 py-1">
      <CommandMenuGroupHeader id={headingId}>
        {group.label}
      </CommandMenuGroupHeader>
      <div className="flex flex-col">{group.options.map(optionRenderer)}</div>
    </CommandGroup>
  );
}
CommandMenuGroupSection.displayName = 'CommandMenu.GroupSection';

function CommandMenuBodyContent({
  children,
  groups,
  options,
  optionRenderer,
}: CommandMenuBodyContentProps) {
  if (children) return children;

  if (groups.length > 0) {
    return groups.map((group, index) => (
      <React.Fragment key={group.id}>
        {index > 0 && <CommandSeparator className="mx-0" />}
        <CommandMenuGroupSection
          group={group}
          optionRenderer={optionRenderer}
        />
      </React.Fragment>
    ));
  }

  return <div className="flex flex-col p-2">{options.map(optionRenderer)}</div>;
}

function CommandMenuBody({
  children,
  groups,
  options,
  onSelect,
  onClose,
  placeholder,
  emptyText,
  emptyState,
  showResults,
  showShortcut,
  shortcutText,
  showFooter,
  footerHints,
  showSettingsButton,
  onSettingsClick,
  maxHeight,
  reserveResultsSpace = false,
  filter,
}: CommandMenuBodyProps) {
  const safeMaxHeight = getCommandMenuMaxHeight(maxHeight);
  const renderOption = (option: CommandMenuOption) => (
    <CommandMenuItem key={option.id} option={option} onSelect={onSelect} />
  );

  return (
    <Command
      filter={filter}
      className="rounded-none border-0 shadow-none"
      data-slot="command-menu"
    >
      <search
        aria-label="Command menu search"
        className="block border-b border-input bg-background"
      >
        <CommandInput
          render={({ clearSearch, value, ...inputProps }) => {
            const canClose = typeof onClose === 'function';
            const clearOrClose = () => {
              if (value) {
                clearSearch();
                return;
              }
              onClose?.();
            };

            return (
              <div
                data-slot="command-menu-input"
                cmdk-input-wrapper=""
                className="flex h-11 items-center gap-2 px-3"
              >
                <Search
                  className="size-4 shrink-0 text-muted-foreground opacity-50"
                  aria-hidden="true"
                />
                <input
                  {...inputProps}
                  value={value}
                  placeholder={placeholder}
                  aria-label={placeholder}
                  className="min-w-0 flex-1 bg-transparent text-sm leading-5 text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                />
                {showShortcut && (
                  <CommandShortcutBadge>{shortcutText}</CommandShortcutBadge>
                )}
                <ClearButton
                  size="icon-xs"
                  className="-me-1 rounded-sm p-0 opacity-50 hover:opacity-80 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4"
                  disabled={!value && !canClose}
                  aria-label={
                    value
                      ? 'Clear search'
                      : canClose
                        ? 'Close command menu'
                        : 'Clear search'
                  }
                  onClick={clearOrClose}
                />
              </div>
            );
          }}
        />
      </search>

      <CommandList
        hidden={!showResults}
        style={{
          maxHeight: safeMaxHeight,
          height: reserveResultsSpace ? safeMaxHeight : undefined,
        }}
        className="overflow-y-auto p-0"
      >
        {showResults && (
          <>
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              {emptyState || emptyText}
            </CommandEmpty>
            <CommandMenuBodyContent
              groups={groups}
              options={options}
              optionRenderer={renderOption}
            >
              {children}
            </CommandMenuBodyContent>
          </>
        )}
      </CommandList>

      {showFooter && (
        <CommandMenuFooter
          hints={footerHints}
          showSettingsButton={showSettingsButton}
          onSettingsClick={onSettingsClick}
        />
      )}
    </Command>
  );
}
CommandMenuBody.displayName = 'CommandMenu.Body';

// ============================================
// Main Command Menu Component
// ============================================

function CommandMenu({
  open,
  onOpenChange,
  placeholder = DEFAULT_COMMAND_MENU_PLACEHOLDER,
  emptyText = 'No results found.',
  emptyState,
  showResults = true,
  options = [],
  groups = [],
  onSelect,
  showShortcut = false,
  shortcutText,
  showFooter = false,
  footerHints,
  showSettingsButton = false,
  onSettingsClick,
  className,
  maxHeight = DEFAULT_COMMAND_MENU_MAX_HEIGHT,
  filter,
  children,
}: CommandMenuProps) {
  const platformShortcut = usePlatformShortcut();
  const resolvedShortcut = shortcutText ?? platformShortcut;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      className={cn('max-w-[508px] sm:max-w-[508px]', className)}
    >
      <CommandMenuBody
        placeholder={placeholder}
        emptyText={emptyText}
        emptyState={emptyState}
        showResults={showResults}
        options={options}
        groups={groups}
        onSelect={onSelect}
        onClose={() => onOpenChange?.(false)}
        reserveResultsSpace
        showShortcut={showShortcut}
        shortcutText={resolvedShortcut}
        showFooter={showFooter}
        footerHints={footerHints}
        showSettingsButton={showSettingsButton}
        onSettingsClick={onSettingsClick}
        maxHeight={maxHeight}
        filter={filter}
      >
        {children}
      </CommandMenuBody>
    </CommandDialog>
  );
}
CommandMenu.displayName = 'CommandMenu';

// ============================================
// Standalone Command Menu (non-dialog version)
// ============================================

export interface CommandMenuPanelProps
  extends Omit<CommandMenuProps, 'open' | 'onOpenChange'> {
  /** Additional class name */
  className?: string;
}

function CommandMenuPanel({
  placeholder = DEFAULT_COMMAND_MENU_PLACEHOLDER,
  emptyText = 'No results found.',
  emptyState,
  showResults = true,
  options = [],
  groups = [],
  onSelect,
  showShortcut = false,
  shortcutText,
  showFooter = false,
  footerHints,
  showSettingsButton = false,
  onSettingsClick,
  className,
  maxHeight = DEFAULT_COMMAND_MENU_MAX_HEIGHT,
  filter,
  children,
}: CommandMenuPanelProps) {
  const platformShortcut = usePlatformShortcut();
  const resolvedShortcut = shortcutText ?? platformShortcut;

  return (
    <div
      data-slot="command-menu-panel"
      className={cn(COMMAND_MENU_SHELL_CLASS_NAME, className)}
    >
      <CommandMenuBody
        placeholder={placeholder}
        emptyText={emptyText}
        emptyState={emptyState}
        showResults={showResults}
        options={options}
        groups={groups}
        onSelect={onSelect}
        showShortcut={showShortcut}
        shortcutText={resolvedShortcut}
        showFooter={showFooter}
        footerHints={footerHints}
        showSettingsButton={showSettingsButton}
        onSettingsClick={onSettingsClick}
        maxHeight={maxHeight}
        filter={filter}
      >
        {children}
      </CommandMenuBody>
    </div>
  );
}
CommandMenuPanel.displayName = 'CommandMenu.Panel';

// ============================================
// Exports
// ============================================

export {
  CommandMenu,
  CommandMenuFooter,
  CommandMenuGroupHeader,
  CommandMenuItem,
  CommandMenuPanel,
  CommandShortcutBadge,
  defaultFooterHints,
  FooterNavIcon,
  FooterNavText,
};
